import {
  Account,
  Mint,
  TOKEN_2022_PROGRAM_ID,
  getAssociatedTokenAddress,
  unpackAccount,
  unpackMint,
  getMint,
} from '@solana/spl-token'
import { AccountInfo, PublicKey } from '@solana/web3.js'
import { useMemo } from 'react'
import { useAsync } from 'react-async-hook'
import { UseAccountState, useAccount } from '@helium/account-fetch-cache-hooks'
import { Provider } from '@coral-xyz/anchor'
import { TOKEN_PROGRAM_ID } from '@coral-xyz/anchor/dist/cjs/utils/token'
import { NATIVE_MINT } from '@solana/spl-token'
const is2022 = async (provider: Provider, mint: PublicKey) => {
  try {
    const mintInfo = await getMint(
      provider.connection,
      mint,
      'confirmed',
      TOKEN_2022_PROGRAM_ID,
    )

    return !!mintInfo
  } catch (e) {
    return false
  }
}

export function useOwnedAmount2022(
  wallet: PublicKey | undefined | null,
  mint: PublicKey | undefined | null,
): {
  amount: bigint | undefined
  decimals: number | undefined
  loading: boolean
} {
  const { associatedAccount, loading: loadingAssoc } =
    useAssociatedTokenAccount(wallet, mint)
  const { info: mintAcc, loading: loadingMint } = useMint2022(
    mint?.toBase58() === NATIVE_MINT.toBase58() ? undefined : mint,
  )

  const amount = useMemo(() => {
    if (associatedAccount) {
      return associatedAccount.amount
    }
  }, [associatedAccount])
  return {
    loading: loadingAssoc || loadingMint,
    amount,
    decimals: mintAcc?.decimals || 9,
  }
}

interface AssocState {
  loading: boolean
  result?: PublicKey
}
const fetch = async (
  wallet: PublicKey | undefined | null,
  mint: PublicKey | undefined | null,
): Promise<PublicKey | undefined> => {
  if (!wallet || !mint) {
    return undefined
  }

  try {
    const associatedTokenAddress = await getAssociatedTokenAddress(
      mint,
      wallet,
      true,
      TOKEN_2022_PROGRAM_ID,
    )
    return associatedTokenAddress
  } catch {
    return undefined
  }
}

export function useAssociatedTokenAddress2022(
  wallet: PublicKey | undefined | null,
  mint: PublicKey | undefined | null,
): AssocState {
  const { result, loading } = useAsync(async () => {
    return fetch(wallet, mint)
  }, [mint?.toBase58(), wallet?.toBase58()])

  return { result, loading }
}

export interface AssociatedAccountState {
  associatedAccount?: Account
  associatedAccountKey?: PublicKey
  loading: boolean
}

/**
 * Get the associcated token account for this wallet, or the account itself is this address is already an ata
 *
 * @param walletOrAta
 * @param mint
 * @returns
 */
export function useAssociatedTokenAccount(
  walletOrAta: PublicKey | undefined | null,
  mint: PublicKey | undefined | null,
): AssociatedAccountState {
  const { result: associatedTokenAddress, loading: loading } =
    useAssociatedTokenAddress2022(walletOrAta, mint)
  const { info: associatedAccount, loading: loading2 } = useTokenAccount2022(
    associatedTokenAddress,
  )

  const result = useMemo(() => {
    if (associatedAccount?.mint === mint) {
      // The passed value is the ata
      return associatedAccount
    } else {
      return associatedAccount
    }
  }, [associatedAccount, mint])

  return {
    associatedAccount: result,
    loading: loading || loading2,
    associatedAccountKey: associatedTokenAddress,
  }
}

const parser = (
  pubkey: PublicKey,
  acct: AccountInfo<Buffer>,
): Account | undefined => {
  try {
    const data = unpackAccount(pubkey, acct, TOKEN_PROGRAM_ID)
    return data
  } catch {
    try {
      const data = unpackAccount(pubkey, acct, TOKEN_2022_PROGRAM_ID)
      return data
    } catch {
      return undefined
    }
  }
}

export function useTokenAccount2022(
  address: PublicKey | undefined | null,
): UseAccountState<Account | undefined> {
  return useAccount(address, parser)
}

export const MintParser = (pubKey: PublicKey, info: AccountInfo<Buffer>) => {
  try {
    const data = unpackMint(pubKey, info, TOKEN_PROGRAM_ID)
    return data
  } catch {
    try {
      const data = unpackMint(pubKey, info, TOKEN_2022_PROGRAM_ID)
      return data
    } catch {
      return undefined
    }
  }
}

export function useMint2022(
  key: PublicKey | undefined | null,
): UseAccountState<Mint | undefined> {
  const accountInfo = useAccount(key, MintParser)

  if (key?.toBase58() === NATIVE_MINT.toBase58()) {
    return {
      info: {
        decimals: 9,
        address: NATIVE_MINT,
        mintAuthority: undefined,
        supply: BigInt(0),
        isInitialized: true,
        freezeAuthority: undefined,
        tlvData: undefined,
      },
      loading: false,
      status: 'success',
    }
  }

  return accountInfo
}
